import { z } from "zod";
import { PricelistDateSchema } from "./pricelist-primitives.contract";

/**
 * The filters the pricelist case LIST and the price-file-log EXPORT share.
 *
 * One schema in its own module because two contracts consume it and neither
 * may import the other: `pricelist-cases.contract` imports the date primitive
 * from `pricelist.contract`, and `pricelist.contract` owns `exportLog`. Sharing
 * the schema is what keeps "everything matching the active filter" meaning the
 * same thing to the list and to a select-all export — if either grew a filter
 * the other lacked, select-all would quietly export a different set than the
 * one on screen.
 *
 * Every filter is optional; an empty object means every case in the
 * organization.
 */

export const PricelistCaseListFiltersSchema = z.object({
  /**
   * Case number (`PC-12`, or just `12`), vendor name, or task title —
   * case-insensitive substring match.
   */
  search: z.string().max(200).optional(),
  /** Narrow to one vendor. */
  companyId: z.uuid().optional(),
  /**
   * Owner user ids plus AR's `UNASSIGNED_ASSIGNEE_FILTER` sentinel; entries
   * are OR'd.
   */
  assignees: z.array(z.string().min(1)).max(50).optional(),
  /** Waiting-on option keys (org-defined), OR'd. */
  waitingOn: z.array(z.string().min(1)).max(50).optional(),
  /** Effective date on or after (YYYY-MM-DD). */
  effectiveFrom: PricelistDateSchema.optional(),
  /** Effective date on or before (YYYY-MM-DD). */
  effectiveTo: PricelistDateSchema.optional(),
  /** Received date on or after (YYYY-MM-DD). */
  receivedFrom: PricelistDateSchema.optional(),
  /** Received date on or before (YYYY-MM-DD). */
  receivedTo: PricelistDateSchema.optional(),
  /**
   * Opened (case `created_at`) on or after (YYYY-MM-DD, UTC calendar day).
   * The "Recent" quick filter (GEN-2580) is this set to a week ago — a
   * plain, visible date filter rather than a hidden lens, so nobody wonders
   * where the older cases went.
   */
  openedFrom: PricelistDateSchema.optional(),
  /** Opened on or before (YYYY-MM-DD, UTC calendar day). */
  openedTo: PricelistDateSchema.optional(),
  /** Only cases whose vendor carries a Ship & Debit module config. */
  shipAndDebit: z.literal(true).optional(),
});

export const PricelistCaseSortBySchema = z.enum([
  "caseNumber",
  "companyName",
  "effectiveDate",
  "receivedDate",
  "fileCount",
  /** By the owner's display name; unowned last. */
  "assignee",
  /** By the stored reason key (org-defined labels have no fixed order); unparked last. */
  "waitingOnKey",
  "followUpAt",
  "filesReadyAt",
  "createdAt",
]);

export type PricelistCaseListFilters = z.infer<
  typeof PricelistCaseListFiltersSchema
>;
export type PricelistCaseSortBy = z.infer<typeof PricelistCaseSortBySchema>;
